import Axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import cookie, { COOKIE, getToken } from './cookie';
import { serializePaginationFromHeader, PaginationModel } from './pagination';
import Vue from 'vue';
import { sendMpEvent } from './mixpanel-analytics/util.mp';
import { MP_VALUE } from './mixpanel-analytics/enum.mp-value';

export enum HttpCode {
  Unauthorized = 401,
}

export interface CustomAxiosResponse extends AxiosResponse {
  pagination: PaginationModel;
}

const config: AxiosRequestConfig = {
  baseURL: process.env.VUE_APP_API_BASE_URL,
  timeout: 20000,
};

const requestInterceptor = (config: AxiosRequestConfig) => {
  config.headers = {
    ...config.headers,
    Authorization: `Bearer ${cookie.get(COOKIE.TOKEN)}`,
  };
  return config;
};

const timeout = 100;
const env = process.env.VUE_APP_ENVIRONMENT;

function responseInterceptorWithTimeout(response: AxiosResponse) {
  return new Promise(resolve => {
    setTimeout(resolve, timeout);
  })
    .then(() => {
      // eslint-disable no-console
      console.log(`ACTIVE_TIMEOUT:${timeout}ms:${env}`);
      return Promise.resolve({
        ...response,
        pagination: serializePaginationFromHeader(response.headers),
      });
    })
    .catch(err => {
      return Promise.reject(err);
    });
}

function responseInterceptor(response: AxiosResponse) {
  return {
    ...response,
    pagination: serializePaginationFromHeader(response.headers),
  };
}

const errorInterceptor = () => {
  return ({ response }: any) => {
    const errorMessage = response && response.data && response.data.message;

    const errorApi =
      response && response.request && response.request.responseURL;

    if (response.status >= 500) {
      sendMpEvent({
        event: MP_VALUE.Error500,
        properties: { error: errorMessage || 'error', api: errorApi || 'api' },
      });
    } else if (response.status >= 400) {
      sendMpEvent({
        event: MP_VALUE.Error400,
        properties: { error: errorMessage || 'error', api: errorApi || 'api' },
      });
    } else if (response.status >= 300) {
      sendMpEvent({
        event: MP_VALUE.Error300,
        properties: { error: errorMessage || 'error', api: errorApi || 'api' },
      });
    }

    const status = response.status;
    if (response && response.status === HttpCode.Unauthorized) {
      const token = getToken();
      if (token) {
        cookie.set(COOKIE.SIGNOUT, token, { sameSite: 'strict' });
        window.location.href = '/';
      }
      cookie.remove(COOKIE.TOKEN, { sameSite: 'Strict' });
    } else if (status >= 500 && status <= 599) {
      Vue.toasted.show(`Error: ${response.data.message}`, {
        className: 'main--toast',
        position: 'top-center',
        duration: 3000,
      });
    }

    return Promise.reject(response.data);
  };
};

const instance = Axios.create(config);
instance.interceptors.request.use(function(config) {
  return requestInterceptor(config);
});

instance.interceptors.response.use(response => {
  if (env === 'development') {
    return responseInterceptorWithTimeout(response);
  } else {
    return responseInterceptor(response);
  }
}, errorInterceptor());

export const axios = instance;
